/**
 * Curved Cushion Configurator — Pricing Engine + Interactive SVG Preview
 * uscushion.com · 2026-07
 *
 * Curved cushion = single arc/crescent piece
 * 6 dimensions: Side Length, Back Width (Curved/Straight), Front Width (Curved/Straight), Thickness
 * Pricing: bench formula with L = Back Width (Straight), W = Side Length (bounding rectangle)
 */
(function () {
  'use strict';

  /* ─── Cost constants (same as bench) ─── */
  var FABRIC_PRICE_RMB  = 25;
  /* Premium fabric grades (RMB per m2); standard falls back to FABRIC_PRICE_RMB. Driven by fabric `grade:` tag. */
  var FABRIC_RATE_RMB   = { marine: 54 };
  function fabricRate() {
    var g = (state.fabric && state.fabric.tags) ? extractTag(state.fabric.tags, 'grade') : '';
    return FABRIC_RATE_RMB[g] || FABRIC_PRICE_RMB;
  }
  var FOAM_PRICE_RMB    = 1750;
  var FOAM_DENSITY      = 35;
  var FABRIC_GSM        = 400;
  var SHIPPING_RMB      = 90;
  var ACCESSORIES_RMB   = 30;
  var EXCHANGE_RATE     = 6.8;
  var PAYMENT_FEE_RATE  = 0.049;
  var TARGET_COST_RATIO = 0.40;
  var MARINE_COST_RATIO = 0.38;   /* Marine fabric target margin 62% (standard 60%); used when grade:marine is selected */
  var COVER_COST_RATIO  = 0.55;   /* Extra covers = 45% margin (AOV add-on, lower than cushion's 60/62%); same for standard & marine */
  function targetRatio() {
    var g = (state.fabric && state.fabric.tags) ? extractTag(state.fabric.tags, 'grade') : '';
    return g === 'marine' ? MARINE_COST_RATIO : TARGET_COST_RATIO;
  }
  var MIN_PRICE         = 45;

  /* ─── Addon pricing (USD) ─── */
  var ADDON = {
    piping:      { none: 0, single: 5, double: 8 },
    pipingColor: { match: 0, white: 5, black: 5, navy: 5, cream: 5 },
    straps:      { none: 0, two: 2, four: 4 },
    zipper:      { regular: 0, invisible: 0 },
    antislip:    { none: 0, black_pad: 5, beige_pad: 5, velcro: 9 },
    throwPillow: {
      none: 0,
      '2pcs_12x20': 29, '2pcs_16x16': 29, '2pcs_18x18': 32,
      '2pcs_20x20': 35, '2pcs_24x24': 38, '2pcs_custom': 40,
      '4pcs_12x20': 51, '4pcs_16x16': 51, '4pcs_18x18': 57,
      '4pcs_20x20': 63, '4pcs_24x24': 67, '4pcs_custom': 70
    },
    pillowCovers: {
      none: 0,
      '2pcs_12x20': 19, '2pcs_16x16': 19, '2pcs_18x18': 21,
      '2pcs_20x20': 24, '2pcs_24x24': 29, '2pcs_custom': 31,
      '4pcs_12x20': 32, '4pcs_16x16': 32, '4pcs_18x18': 36,
      '4pcs_20x20': 42, '4pcs_24x24': 52, '4pcs_custom': 55
    },
    armrestPillow: { none: 0, square_2pcs: 30, round_2pcs: 30 }
  };
  /* ─── Units (depolster.de): inputs in cm, pricing engine unchanged (inch). inch→cm only for display. ─── */
  var CM_PER_IN = 2.54;
  function parseNum(v) { return parseFloat(String(v == null ? '' : v).replace(',', '.')) || 0; }
  function round1(n) { return Math.round(n * 10) / 10; }
  var PILLOW_SIZE = { '12x20': '30×50 cm', '16x16': '40×40 cm', '18x18': '45×45 cm', '20x20': '50×50 cm', '24x24': '60×60 cm', 'custom': 'Sondermaß' };
  function pillowLabel(v) { var m = /^(\d)pcs_(.+)$/.exec(v); return m ? (m[1] + ' Stück ' + (PILLOW_SIZE[m[2]] || m[2])) : v; }
  var ARMREST_LABEL = { square_2pcs: 'Eckig — 2 Stück', round_2pcs: 'Rund — 2 Stück' };


  var LABELS = {
    none: 'Keine', single: 'Einfacher Keder', double: 'Doppelter Keder',
    match: 'Passend zum Stoff', white: 'Weißer Keder', black: 'Schwarzer Keder', navy: 'Marineblauer Keder', cream: 'Cremefarbener Keder',
    two: '2 Bänder', four: '4 Bänder',
    regular: 'Standard-Reißverschluss', invisible: 'Nahtverdeckter Reißverschluss',
    velcro: 'Klettband', black_pad: 'Schwarze Unterlage', beige_pad: 'Beige Unterlage'
  };

  var FABRIC_COLORS = {
    'blue':'#2c3e6b','navy':'#1a2540','teal':'#2a7a6b','green':'#4a7a2a',
    'sage':'#a8b89a','red':'#b83232','coral':'#d85a45','orange':'#d4783a',
    'yellow':'#d4a83a','gold':'#c8963a','pink':'#d4649a','purple':'#6a4a9a',
    'lavender':'#9a8ab8','brown':'#7a5a3a','camel':'#c8a878','tan':'#c8b08a',
    'beige':'#d4c8a8','cream':'#ede8df','ivory':'#f0ead8','white':'#f5f5f0',
    'gray':'#9a9a9a','grey':'#9a9a9a','charcoal':'#4a4a4a','black':'#1a1a1a',
    'mint':'#98d4b4','rust':'#b7502a','mustard':'#c8a020','olive':'#6b702a',
    'blush':'#de9898','mauve':'#b07898','burgundy':'#6b1a2a','taupe':'#8a7a6a',
    'sand':'#c8b898','silver':'#bfc0c2','steel blue':'#4a7a98',
    'dark olive':'#4a4a2a','dark green':'#2a5a2a','dark brown':'#4a3020',
    'dark blue':'#1a2a5a','dark teal':'#1a5a5a','dark gray':'#5a5a5a',
    'light gray':'#c8c8c8','gray blue':'#6a7a8a','yellow green':'#8ab82a',
    'gray black':'#3a3a3a'
  };

  /* ─── State ─── */
  var state = {
    sideLength: 0, backWidthCurved: 0, backWidthStraight: 0,
    frontWidthCurved: 0, frontWidthStraight: 0, thickness: 0,
    sideLengthcm: 0, backWidthCurvedcm: 0, backWidthStraightcm: 0, frontWidthCurvedcm: 0, frontWidthStraightcm: 0, thicknesscm: 0,
    fabric: null, piping: '', pipingColor: 'match', straps: '',
    zipper: 'regular', zipperPlacement: '', antislip: '',
    extraCovers: '0', throwPillow: 'none', pillowCovers: 'none',
    armrestPillow: 'none', notes: ''
  };

  var els = {};

  /* ─── Color helpers ─── */
  function hexToRgb(hex) {
    hex = hex.replace('#', '');
    return [parseInt(hex.substring(0, 2), 16), parseInt(hex.substring(2, 4), 16), parseInt(hex.substring(4, 6), 16)];
  }
  function rgbToHex(r, g, b) {
    return '#' + [r, g, b].map(function (c) {
      var x = Math.round(Math.max(0, Math.min(255, c))).toString(16);
      return x.length === 1 ? '0' + x : x;
    }).join('');
  }
  function lighten(hex, r) { var c = hexToRgb(hex); return rgbToHex(c[0] + (255 - c[0]) * r, c[1] + (255 - c[1]) * r, c[2] + (255 - c[2]) * r); }
  function darken(hex, r)  { var c = hexToRgb(hex); return rgbToHex(c[0] * (1 - r), c[1] * (1 - r), c[2] * (1 - r)); }
  function extractTag(tags, prefix) {
    if (!tags) return '';
    for (var i = 0; i < tags.length; i++) {
      var t = (typeof tags[i] === 'string' ? tags[i] : '').toLowerCase().trim();
      if (t.indexOf(prefix + ':') === 0) return tags[i].split(':')[1].trim().toLowerCase();
    }
    return '';
  }

  /* ─── Pricing (bounding rectangle: backWidthStraight × sideLength × thickness) ─── */
  function calcBasePrice(L, W, T) {
    if (!L || !W || !T) return null;
    var Lm = L / 39.37, Wm = W / 39.37, Tm = T / 39.37;
    var fabricArea = Lm * Wm * 2 + Lm * Tm * 2 + Wm * Tm * 2;
    var foamVolume = Lm * Wm * Tm;
    var totalWeight = foamVolume * FOAM_DENSITY + fabricArea * FABRIC_GSM / 1000;
    var totalRMB = fabricArea * fabricRate() + foamVolume * FOAM_PRICE_RMB + totalWeight * SHIPPING_RMB + ACCESSORIES_RMB;
    return Math.max(totalRMB / EXCHANGE_RATE / (1 - PAYMENT_FEE_RATE) / targetRatio() * (window.__CC_COVER_FACTOR || 1), MIN_PRICE);
  }

  /* One extra cover = a fabric-only shell (no foam), bounding rectangle
     backWidthStraight × sideLength, fabric grade auto via fabricRate, at
     COVER_COST_RATIO (45% margin). Replaces the flat $30/$50 fee. */
  function extraCoverUnit() {
    var L = state.backWidthStraight, W = state.sideLength, T = state.thickness;
    if (!L || !W || !T) return 0;
    var Lm = L / 39.37, Wm = W / 39.37, Tm = T / 39.37;
    var fabricArea = Lm * Wm * 2 + Lm * Tm * 2 + Wm * Tm * 2;
    var weight = fabricArea * FABRIC_GSM / 1000; // fabric weight only — no foam
    var totalRMB = fabricArea * fabricRate() + weight * SHIPPING_RMB + ACCESSORIES_RMB;
    return Math.round(totalRMB / EXCHANGE_RATE / (1 - PAYMENT_FEE_RATE) / COVER_COST_RATIO);
  }

  function updateExtraCoverLabels() {
    if (!els.extraCovers) return;
    var unit = extraCoverUnit();
    var opts = els.extraCovers.options;
    for (var i = 0; i < opts.length; i++) {
      var n = parseInt(opts[i].value) || 0;
      if (n === 0) { opts[i].text = 'Kein zusätzlicher Bezug'; continue; }
      var label = (n === 1 ? '1 Set' : n + ' Sets');
      opts[i].text = unit > 0 ? label + ' (+' + (unit * n) + ' €)' : label;
    }
  }

  function getAddonTotal() {
    return (ADDON.piping[state.piping] || 0)
      + (ADDON.pipingColor[state.pipingColor] || 0)
      + (ADDON.straps[state.straps] || 0) + (ADDON.zipper[state.zipper] || 0)
      + (ADDON.antislip[state.antislip] || 0) + extraCoverUnit() * (parseInt(state.extraCovers) || 0)
      + (ADDON.throwPillow[state.throwPillow] || 0)
      + (ADDON.pillowCovers[state.pillowCovers] || 0)
      + (ADDON.armrestPillow[state.armrestPillow] || 0);
  }

  function hasDims() {
    return state.sideLength >= 6 && state.backWidthCurved >= 6 && state.backWidthStraight >= 6
      && state.frontWidthCurved >= 6 && state.frontWidthStraight >= 6 && state.thickness >= 1;
  }

  function isComplete() {
    return hasDims()
      && state.fabric && state.piping && state.straps && state.antislip;
  }

  /* ── No SVG model for curved — use static reference image instead ── */
  function drawCushion() {}

  /* ─── Summary ─── */
  function updateSummary() {
    if (!els.summary) return;
    var parts = [];
    if (state.sideLength && state.backWidthStraight && state.frontWidthStraight && state.thickness)
      parts.push('Seite ' + round1(state.sideLengthcm) + ' cm · Hinten ' + round1(state.backWidthStraightcm) + ' cm · Vorne ' + round1(state.frontWidthStraightcm) + ' cm · H ' + round1(state.thicknesscm) + ' cm');
    if (state.fabric) parts.push(state.fabric.title);
    if (state.piping && state.piping !== 'none') {
      var pipLabel = LABELS[state.piping];
      if (state.pipingColor && state.pipingColor !== 'match') pipLabel += ' (' + (LABELS[state.pipingColor] || state.pipingColor) + ')';
      parts.push(pipLabel);
    }
    if (state.straps && state.straps !== 'none') parts.push(LABELS[state.straps]);
    if (state.antislip && state.antislip !== 'none') parts.push(LABELS[state.antislip]);
    els.summary.textContent = parts.length ? parts.join(' · ') : 'Konfiguriere unten dein halbrundes Polster';
  }

  /* ─── Recalc ─── */
  function recalc() {
    var _cf = window.__ucfs_selectedFabric || window._ucfsSelected;
    if (_cf && _cf !== state.fabric) state.fabric = _cf;
    state.sideLengthcm = parseNum(els.sideLength.value); state.sideLength = state.sideLengthcm / CM_PER_IN;
    state.backWidthCurvedcm = parseNum(els.backWidthCurved.value); state.backWidthCurved = state.backWidthCurvedcm / CM_PER_IN;
    state.thicknesscm = parseNum(els.thickness.value); state.thickness = state.thicknesscm / CM_PER_IN;
    state.backWidthStraightcm = parseNum(els.backWidthStraight.value); state.backWidthStraight = state.backWidthStraightcm / CM_PER_IN;
    state.frontWidthCurvedcm = parseNum(els.frontWidthCurved.value); state.frontWidthCurved = state.frontWidthCurvedcm / CM_PER_IN;
    state.frontWidthStraightcm = parseNum(els.frontWidthStraight.value); state.frontWidthStraight = state.frontWidthStraightcm / CM_PER_IN;
    state.extraCovers = els.extraCovers.value;
    state.throwPillow = els.throwPillow ? els.throwPillow.value : 'none';
    state.pillowCovers = els.pillowCovers ? els.pillowCovers.value : 'none';
    state.armrestPillow = els.armrestPillow ? els.armrestPillow.value : 'none';
    state.zipperPlacement = els.zipperPlacement ? els.zipperPlacement.value : '';
    state.pipingColor = els.pipingColor ? els.pipingColor.value : 'match';
    state.notes = els.notes.value;

    if (els.pipingColorWrap) {
      var showPipingColor = (state.piping === 'single' || state.piping === 'double');
      els.pipingColorWrap.style.display = showPipingColor ? '' : 'none';
      if (!showPipingColor) {
        state.pipingColor = 'match';
        if (els.pipingColor) els.pipingColor.value = 'match';
      }
    }

    // Validation highlights
    ['sideLength', 'backWidthCurved', 'thickness', 'backWidthStraight', 'frontWidthCurved', 'frontWidthStraight'].forEach(function (id) {
      els[id].classList.toggle('ok', parseFloat(els[id].value) > 0);
    });

    // Constraint warnings: curved must be >= straight
    if (state.backWidthCurved && state.backWidthStraight && state.backWidthCurved < state.backWidthStraight) {
      els.backWidthCurved.style.borderColor = '#e74c3c';
    } else {
      els.backWidthCurved.style.borderColor = '';
    }
    if (state.frontWidthCurved && state.frontWidthStraight && state.frontWidthCurved < state.frontWidthStraight) {
      els.frontWidthCurved.style.borderColor = '#e74c3c';
    } else {
      els.frontWidthCurved.style.borderColor = '';
    }

    updateSummary();

    var dimOk = hasDims();
    var ec = parseInt(state.extraCovers) || 0;

    // Pricing: bounding rectangle = backWidthStraight × sideLength × thickness
    updateExtraCoverLabels();
    var base = dimOk ? calcBasePrice(state.backWidthStraight, state.sideLength, state.thickness) : null;
    var addonTotal = getAddonTotal();

    if (base !== null) {
      var finalPrice = Math.round(base + addonTotal);
      els.priceMain.textContent = finalPrice + ' €';
      var parts = ['Grundpreis ' + Math.round(base) + ' €'];
      if (ADDON.piping[state.piping])     parts.push('Keder +' + ADDON.piping[state.piping] + ' €');
      if (ADDON.pipingColor[state.pipingColor]) parts.push('Keder-Farbe +' + ADDON.pipingColor[state.pipingColor] + ' €');
      if (ADDON.straps[state.straps])     parts.push('Bänder +' + ADDON.straps[state.straps] + ' €');
      if (ADDON.zipper[state.zipper])     parts.push('Reißverschluss +' + ADDON.zipper[state.zipper] + ' €');
      if (ADDON.antislip[state.antislip]) parts.push('Anti-Rutsch +' + ADDON.antislip[state.antislip] + ' €');
      if (ec > 0)                         parts.push('Zusätzliche Bezüge +' + (extraCoverUnit() * ec) + ' €');
      if (ADDON.throwPillow[state.throwPillow])   parts.push('Dekokissen +' + ADDON.throwPillow[state.throwPillow] + ' €');
      if (ADDON.pillowCovers[state.pillowCovers]) parts.push('Kissenbezüge +' + ADDON.pillowCovers[state.pillowCovers] + ' €');
      if (ADDON.armrestPillow[state.armrestPillow]) parts.push('Armlehnenkissen +' + ADDON.armrestPillow[state.armrestPillow] + ' €');
      els.priceBreak.textContent = parts.join(' · ');
    } else {
      els.priceMain.textContent = '— €';
      els.priceBreak.textContent = 'Alle 6 Maße eingeben, um den Preis zu sehen';
    }

    var complete = isComplete();
    var btns = [els.addBtn, els.addBtnSticky].filter(Boolean);
    btns.forEach(function (btn) {
      btn.disabled = !complete;
      if (complete && base !== null) {
        var fp = Math.round(base + addonTotal);
        btn.textContent = 'Zur Bestellung hinzufügen — ' + fp + ' € →';
      } else {
        btn.textContent = 'Zur Bestellung hinzufügen →';
      }
    });

    if (els.addNote) {
      if (complete) {
        els.addNote.textContent = 'Kostenloser Versand · Versand in ca. 20 Werktagen';
      } else {
        var missing = [];
        if (!dimOk) missing.push('Maße');
        if (!state.fabric) missing.push('Stoff');
        if (!state.piping) missing.push('Keder');
        if (!state.straps) missing.push('Bänder');
        if (!state.antislip) missing.push('Anti-Rutsch');
        els.addNote.textContent = missing.length ? missing.join(', ') + ' auswählen, um fortzufahren' : '';
      }
    }

    document.dispatchEvent(new CustomEvent('configurator:price-updated', {
      detail: {
        price: base !== null ? Math.round(base + addonTotal) : null,
        state: state
      }
    }));
  }

  /* ─── Card selection ─── */
  function pickCard(el, group, value) {
    el.parentElement.querySelectorAll('.cc-opt-card').forEach(function (c) { c.classList.remove('sel'); });
    el.classList.add('sel');
    state[group] = value;
    recalc();
  }

  /* ─── Line properties for cart ─── */
  function getLineProperties() {
    var ec = parseInt(state.extraCovers) || 0;
    var base = calcBasePrice(state.backWidthStraight, state.sideLength, state.thickness);
    var finalPrice = Math.round(base + getAddonTotal());
    var props = {
      'Form': 'Gebogen',
      'Seitenlänge': round1(state.sideLengthcm) + ' cm',
      'Breite hinten — gebogen': round1(state.backWidthCurvedcm) + ' cm',
      'Breite hinten — gerade': round1(state.backWidthStraightcm) + ' cm',
      'Breite vorne — gebogen': round1(state.frontWidthCurvedcm) + ' cm',
      'Breite vorne — gerade': round1(state.frontWidthStraightcm) + ' cm',
      'Höhe': round1(state.thicknesscm) + ' cm',
      '_Maße (inch)': round1(state.sideLength) + '×' + round1(state.backWidthStraight) + '×' + round1(state.frontWidthStraight) + '×' + round1(state.thickness) + '"',
      'Keder': LABELS[state.piping] || state.piping,
      'Keder-Farbe': (state.pipingColor && state.pipingColor !== 'match') ? (LABELS[state.pipingColor] || state.pipingColor) : 'Passend zum Stoff',
      'Bänder': LABELS[state.straps] || state.straps,
      'Reißverschluss': LABELS[state.zipper] || state.zipper,
      'Anti-Rutsch': LABELS[state.antislip] || state.antislip
    };
    if (state.fabric) {
      props['Stoff'] = state.fabric.title || '';
      props['_Fabric SKU'] = (state.fabric.variants && state.fabric.variants[0] && state.fabric.variants[0].sku) || '';
      props['_Fabric Handle'] = state.fabric.handle || '';
      props['_Fabric Image'] = (state.fabric.images && state.fabric.images[0] && state.fabric.images[0].src) || '';
      var _grade = extractTag(state.fabric.tags || [], 'grade');
      if (_grade) props['Stoffqualität'] = _grade.charAt(0).toUpperCase() + _grade.slice(1);
    }
    if (ec > 0) props['Zusätzliche Bezüge'] = ec + (ec === 1 ? ' Set' : ' Sets') + ' (+' + (extraCoverUnit() * ec) + ' €)';
    if (state.throwPillow && state.throwPillow !== 'none')
      props['Dekokissen'] = pillowLabel(state.throwPillow) + ' (+' + ADDON.throwPillow[state.throwPillow] + ' €)';
    if (state.pillowCovers && state.pillowCovers !== 'none')
      props['Kissenbezüge'] = pillowLabel(state.pillowCovers) + ' (+' + ADDON.pillowCovers[state.pillowCovers] + ' €)';
    if (state.armrestPillow && state.armrestPillow !== 'none')
      props['Armlehnenkissen'] = ARMREST_LABEL[state.armrestPillow] || state.armrestPillow + ' (+' + ADDON.armrestPillow[state.armrestPillow] + ' €)';
    if (state.zipperPlacement) {
      props['Reißverschluss-Position'] = state.zipperPlacement;
    }
    if (state.notes.trim()) props['Anmerkung'] = state.notes.trim();
    props['_Source Product'] = (document.getElementById('cc-configurator') || { dataset: {} }).dataset.productTitle || document.title.split('–')[0].split('—')[0].trim() || location.pathname;

    return { properties: props, price: finalPrice };
  }

  /* ─── DOM transformation (adapt shared Liquid section for Curved) ─── */
  function transformDOM() {
    // Replace SVG with static reference image
    var svg = document.getElementById('cc-dim-svg');
    if (svg) {
      var img = document.createElement('img');
      img.src = 'https://cdn.shopify.com/s/files/1/0676/5687/6232/files/custom_curved_cushion-firepit-measurement_guide.webp?v=1777088497';
      img.alt = 'Maßanleitung gebogenes Polster';
      img.style.cssText = 'width:100%;max-width:480px;display:block;margin:0 auto;border-radius:8px;';
      svg.parentNode.replaceChild(img, svg);
    }

    // Hide Shape section — curved is always one shape
    var shapeSection = document.getElementById('cc-shape-section');
    if (shapeSection) shapeSection.style.display = 'none';

    // Rename seat dimension labels
    // cc-dim-l → Side Length
    var labelL = document.querySelector('label[for="cc-dim-l"]');
    if (labelL) labelL.innerHTML = 'Seitenlänge (cm) <span class="cc-req">*</span>';
    var inputL = document.getElementById('cc-dim-l');
    if (inputL) { inputL.placeholder = 'z. B. 45'; inputL.max = '250'; }

    // cc-dim-w → Back Width (Curved)
    var labelW = document.querySelector('label[for="cc-dim-w"]');
    if (labelW) labelW.innerHTML = 'Breite hinten — gebogen (cm) <span class="cc-req">*</span>';
    var inputW = document.getElementById('cc-dim-w');
    if (inputW) { inputW.placeholder = 'z. B. 180'; inputW.max = '500'; }

    // cc-dim-t → Thickness
    var labelT = document.querySelector('label[for="cc-dim-t"]');
    if (labelT) labelT.innerHTML = 'Höhe (cm) <span class="cc-req">*</span>';

    // Show back dims row for remaining 3 inputs
    var backDims = document.querySelectorAll('.cc-back-dims');
    for (var i = 0; i < backDims.length; i++) {
      var el = backDims[i];
      el.style.display = el.classList.contains('cc-sp-dims') ? 'grid' : 'block';
    }

    // Hide "Back cushion" label
    var backLabel = document.querySelector('.cc-back-label');
    if (backLabel) backLabel.style.display = 'none';

    // cc-back-l → Back Width (Straight)
    var backLLabel = document.querySelector('label[for="cc-back-l"]');
    if (backLLabel) backLLabel.innerHTML = 'Breite hinten — gerade (cm) <span class="cc-req">*</span>';
    var inputBL = document.getElementById('cc-back-l');
    if (inputBL) { inputBL.placeholder = 'z. B. 150'; inputBL.max = '500'; }

    // cc-back-w → Front Width (Curved)
    var backWLabel = document.querySelector('label[for="cc-back-w"]');
    if (backWLabel) backWLabel.innerHTML = 'Breite vorne — gebogen (cm) <span class="cc-req">*</span>';
    var inputBW = document.getElementById('cc-back-w');
    if (inputBW) { inputBW.placeholder = 'z. B. 125'; inputBW.max = '500'; inputBW.style.display = ''; }
    var backWField = inputBW ? inputBW.closest('.cc-field') : null;
    if (backWField) backWField.style.display = '';

    // cc-back-t → Front Width (Straight)
    var backTLabel = document.querySelector('label[for="cc-back-t"]');
    if (backTLabel) backTLabel.innerHTML = 'Breite vorne — gerade (cm) <span class="cc-req">*</span>';
    var inputBT = document.getElementById('cc-back-t');
    if (inputBT) { inputBT.placeholder = 'z. B. 100'; inputBT.max = '500'; inputBT.style.display = ''; }
    var backTField = inputBT ? inputBT.closest('.cc-field') : null;
    if (backTField) backTField.style.display = '';

    // Hide price area above SVG
    var spTop = document.querySelector('.cc-sp-top');
    if (spTop) spTop.style.display = 'none';
    var priceBreakDiv = document.getElementById('cc-price-break');
    if (priceBreakDiv) priceBreakDiv.style.display = 'none';

    // Tighten label spacing
    var dimsLabels = document.querySelectorAll('.cc-dims-label');
    for (var j = 0; j < dimsLabels.length; j++) {
      dimsLabels[j].style.marginBottom = '0';
      dimsLabels[j].style.paddingBottom = '0';
    }

    // Move dimension inputs out of sticky container
    var stickyWrap = document.getElementById('cc-sticky-preview');
    if (stickyWrap) {
      var dimsContainer = document.createElement('div');
      dimsContainer.className = 'cc-dims-outside';

      var seatDims = stickyWrap.querySelector('.cc-sp-dims:not(.cc-back-dims)');
      var backDimsEl = stickyWrap.querySelector('.cc-sp-dims.cc-back-dims');
      var hint = stickyWrap.querySelector('.cc-sp-hint');

      if (seatDims) dimsContainer.appendChild(seatDims);
      if (backDimsEl) dimsContainer.appendChild(backDimsEl);
      if (hint) dimsContainer.appendChild(hint);

      stickyWrap.parentNode.insertBefore(dimsContainer, stickyWrap.nextSibling);
    }

    // Renumber steps since Shape (step 1) is hidden
    var secHeads = document.querySelectorAll('.cc-sec-head .cc-sn');
    var stepNum = 1;
    for (var k = 0; k < secHeads.length; k++) {
      var sec = secHeads[k].closest('.cc-sec');
      if (sec && sec.style.display !== 'none') {
        secHeads[k].textContent = stepNum++;
      }
    }
  }

  /* ─── Init ─── */
  function init() {
    var wrap = document.getElementById('cc-configurator');
    if (!wrap) return;

    transformDOM();

    els.priceMain        = document.getElementById('cc-price-main');
    els.priceBreak       = document.getElementById('cc-price-break');
    els.summary          = document.getElementById('cc-sp-summary');
    els.sideLength       = document.getElementById('cc-dim-l');
    els.backWidthCurved  = document.getElementById('cc-dim-w');
    els.thickness        = document.getElementById('cc-dim-t');
    els.backWidthStraight = document.getElementById('cc-back-l');
    els.frontWidthCurved  = document.getElementById('cc-back-w');
    els.frontWidthStraight = document.getElementById('cc-back-t');
    els.extraCovers      = document.getElementById('cc-extra-covers');
    els.throwPillow      = document.getElementById('cc-throw-pillow');
    els.pillowCovers     = document.getElementById('cc-pillow-covers');
    els.armrestPillow    = document.getElementById('cc-armrest-pillow');
    els.zipperPlacement  = document.getElementById('cc-zipper-placement');
    els.pipingColor      = document.getElementById('cc-piping-color');
    els.pipingColorWrap  = document.getElementById('cc-piping-color-wrap');
    els.notes            = document.getElementById('cc-notes');
    els.addBtn           = document.getElementById('cc-add-btn');
    els.addBtnSticky     = document.getElementById('cc-add-btn-sticky');
    els.addNote          = document.getElementById('cc-add-note');

    wrap.addEventListener('click', function (e) {
      var card = e.target.closest('.cc-opt-card');
      if (!card || !card.dataset.group) return;
      pickCard(card, card.dataset.group, card.dataset.value);
    });

    ['sideLength', 'backWidthCurved', 'thickness', 'backWidthStraight', 'frontWidthCurved', 'frontWidthStraight'].forEach(function (id) {
      if (els[id]) els[id].addEventListener('input', recalc);
    });
    els.extraCovers.addEventListener('change', recalc);
    if (els.throwPillow) els.throwPillow.addEventListener('change', recalc);
    if (els.pillowCovers) els.pillowCovers.addEventListener('change', recalc);
    if (els.armrestPillow) els.armrestPillow.addEventListener('change', recalc);
    if (els.zipperPlacement) els.zipperPlacement.addEventListener('change', recalc);
    if (els.pipingColor) els.pipingColor.addEventListener('change', recalc);
    els.notes.addEventListener('input', recalc);

    document.addEventListener('fabric:selected', function (e) {
      state.fabric = e.detail;
      recalc();
    });
    var existingFabric = window.__ucfs_selectedFabric || window._ucfsSelected;
    if (!state.fabric && existingFabric) {
      state.fabric = existingFabric;
      recalc();
    }
    setInterval(function () {
      var f = window.__ucfs_selectedFabric || window._ucfsSelected;
      if (f && f !== state.fabric) {
        state.fabric = f;
        recalc();
      }
    }, 800);

    function handleAdd() {
      if (!isComplete()) return;
      document.dispatchEvent(new CustomEvent('configurator:add-to-cart', { detail: getLineProperties() }));
    }
    if (els.addBtn) els.addBtn.addEventListener('click', handleAdd);
    if (els.addBtnSticky) els.addBtnSticky.addEventListener('click', handleAdd);

    var defaultZipper = wrap.querySelector('.cc-opt-card[data-group="zipper"][data-value="regular"]');
    if (defaultZipper) defaultZipper.classList.add('sel');

    recalc();
  }

  window.CurvedCushionConfigurator = {
    getState: function () { return state; },
    getLineProperties: getLineProperties,
    recalc: recalc
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
